<template>
  <tile class="opc-simple-edit-base">
    <slot name="header-before" />
    <!-- Product header -->
    <simple-header
      :title="planTitle"
      :icon="logo"
      :show-right-button="showSwitchPlanButton"
      @click-right-button="handleSwitchPlan"
    >
      <template #subTitle v-if="$slots.headerSubtitle">
        <slot name="header-subtitle" />
      </template>
      <template #description>
        <slot name="header-description" />
      </template>
    </simple-header>

    <!-- Divider -->
    <div class="opc-simple-edit-base__divider" v-if="showCapacity" />

    <!-- Number of participants / Number of attendees -->
    <capacity-select
      v-if="showCapacity"
      :value="formData.capacity"
      @change="handleFieldChange('capacity', $event)"
      :label="capacityLabel || $t('billing.simple_edit.number_of_capacity')"
      :helper="capacityDesc"
      :options="selectableCapacities"
      class="opc-simple-edit-base__field"
    />

    <!-- Divider -->
    <div class="opc-simple-edit-base__divider" v-if="showLicenses" />

    <!-- Number of licenses (Workplace Pro/Business, Scheduler) -->
    <slot name="license-before" />
    <div v-if="showLicenses">
      <license-number
        :model-value="formData.licenses"
        @update:model-value="handleFieldChange('licenses', $event)"
        :type="planType"
        :label="licensesLabel"
        :helper="licensesDesc"
        :min="mx_getMinHosts.value"
        :max="mx_getMaxHosts.value"
        :tooltip="licensesTooltip"
        :tooltip-label="licensesTooltipLabel"
        :title="planTitle"
        :tracking-plan-key="trackingPlanKey"
        :unit="currentUnit"
        :data-testid="`checkout-input-license-${selectedPlan ? selectedPlan.plan_code : ''}`"
      />
      <zm-alert v-if="licenseAlert" class="reason-tips" type="info">
        <p v-html="licenseAlert"></p>
      </zm-alert>
    </div>

    <!-- Divider -->
    <div class="opc-simple-edit-base__divider" v-if="showStorage"></div>

    <!-- Cloud Storage special mode -->
    <storage-select
      v-if="showStorage"
      :value="formData.capacity"
      :options="selectableStorages"
      :recommended-capacity="recommendedCapacity"
      :calculate-is-option-disabled="calculateIsOptionDisabled"
      @change="handleStorageChange"
    />

    <!-- Divider -->
    <div class="opc-simple-edit-base__divider" v-if="showCycle"></div>

    <!-- Subscription cycle -->
    <div class="opc-simple-edit-base__subscription" v-if="showCycle">
      <div class="opc-simple-edit-base__subscription-label">{{ $t("billing.simplify.subscription_cycle") }}</div>
      <billed-billing-cycle-radio
        v-model="formData.cycle"
        :billing-cycle-list="selectableBillingCycles"
        :show-annual-term-monthly="showAnnualTermMonthly"
        :unit="currentUnit"
        @change="handleSubscriptionCycleChange"
      />
      <zm-alert class="reason-tips" v-if="cycleAlert" type="info">
        <p v-html="cycleAlert"></p>
      </zm-alert>
      <slot name="cycle-after" />
    </div>

    <!-- Footer actions -->
    <div class="opc-simple-edit-base__footer">
      <slot name="footer" />
    </div>
  </tile>
</template>

<script>
import Tile from "../tile.vue";
import BilledBillingCycleRadio from "@/components/service/config-billing-cycle/billed-cycle/index.vue";
import LicenseNumber from "./license-number.vue";
import SimpleHeader from "./simple-header.vue";
import CapacitySelect from "./capacity-select.vue";
import { mapActions, mapGetters } from "vuex";
import _, { get, sortBy, uniq, debounce } from "lodash";
import {
  BILLING_CYCLE,
  DEFAULT_MAX_HOST,
  MRS_STATUS_TYPE_MAP,
  planTypes,
  simpleEditAutoAddTypes,
  TrackingBillingCycleKeys,
  TrackingBillingCycleUnitKeys, enableProAnnualWithMonthlyPaymentPre, enableProAnnualMonthlyPaymentPlanP2,
} from "@/common/constants";
import { planUnitMap } from "@/common/product-unit";
import autoAddMixin from "@/common/mixin/autoAdd";
import standardPriceMixin from "@/components/capacity/mx/standard-price";
import addon from "@/plans/mx/addon";
import { getPlanTotalLicense } from "@/common/service/validate";
import simplifyForProMixin from "@/plans/ZOOM_MEETING/mixin/simplify-for-pro.js";
import { ZOOM_MEETING_SUB_TYPES } from "@/plans/ZOOM_MEETING/common/utils";
import StorageSelect from "./storage-select.vue";
import { checkMRSChange } from "@/utils/validator";
import { toThousands } from "@/utils/money";
import { getTrackingProductNameByPlanType } from '@/aspects/trackings';

export default {
  name: "SimpleEditBase",
  mixins: [autoAddMixin, addon, standardPriceMixin, simplifyForProMixin],
  components: {
    Tile,
    BilledBillingCycleRadio,
    LicenseNumber,
    SimpleHeader,
    CapacitySelect,
    StorageSelect,
  },
  props: {
    // Product header
    logo: [String, Object],
    planType: String,
    filterCategory: Array,
    // Display control
    showCapacity: {
      type: Boolean,
      default: false,
    },
    showLicenses: {
      type: Boolean,
      default: true,
    },
    showCycle: {
      type: Boolean,
      default: true,
    },
    showStorage: {
      type: Boolean,
      default: false,
    },
    // Switch plan button
    showSwitchPlanButton: {
      type: Boolean,
      default: false,
    },
    // Participants field configuration
    capacityLabel: String,
    // Licenses field configuration
    licensesMin: Number,
    licensesMax: Number,
    licensesTooltip: String,
    licensesTooltipLabel: String,
    minHostsAndReason: Object,
    recommendedCapacity: Object,
    getCycleDisableInfo: Function,
  },
  data() {
    return {
      formData: {
        capacity: null,
        licenses: 1,
        cycle: BILLING_CYCLE.ANNUAL,
      },
      licenseAlert: "",
      //for auto add
      title: "",
    };
  },
  watch: {
    formData: {
      deep: true,
      immediate: true,
      handler: debounce(function(nv, ov) {
        //first return
        if (!ov) return;

        const oldCode = this.selectedPlan?.plan_code;
        const capacityEqualFun = nv.capacity ? (p) => p.capacity === nv.capacity : () => true;
        const cycleEqualFun = nv.cycle ? (p) => p.billing_cycle === nv.cycle : () => true;
        const newPlan = this.selectablePlanList.find((p) => capacityEqualFun(p) && cycleEqualFun(p));
        //if current is in migrate flow, don't excute switchSelectPlanAction
        if (this.migratePlanState.is.pro2biz || this.migratePlanState.is.biz2pro || !this.migratePlanState.flow) {
          this.switchSelectPlanAction([oldCode, { ...newPlan, cartInfo: { selected_number: nv.licenses || 1 } }]);
        }
      }, 200),
    },
    //for switch plan case
    selectedPlan: {
      deep: true,
      immediate: true,
      handler(nv) {
        this.initForm();
        this.mxCheckCollapseSimplifyView({
          type: nv.type,
          billingCycle: nv.billing_cycle,
          selectedNumber: nv.cartInfo?.selected_number,
          capacity: nv.capacity,
        });
      },
    },
    //for msq
    oldPlanCode: {
      deep: true,
      immediate: true,
      handler(nv, ov) {
        //first skip
        if(!ov) return;
        this.mxCheckCollapseSimplifyView({}, true)
      }
    }
  },
  computed: {
    ...mapGetters(["getSelectedPlans", "planMapFlat", "currentEditPlan", "boughtPlans", "getBoughtMeetingPlans", "getCurrentBasePlanInfo", "oldPlanCode", "migratePlanState"]),
    currentBasePlans() {
      return this.getBoughtMeetingPlans;
    },
    currentBoughtPlan() {
      return this.boughtPlans.find(p => p.plan_code === this.selectedPlan?.plan_code) ?? this.currentEditPlan;
    },
    selectedPlan() {
      return this.getSelectedPlans[0];
    },
    selectablePlanList() {
      const filterFun = this.filterCategory?.length ? (p) => this.filterCategory.includes(p.category) && p.type === this.planType : (p) => p.type === this.planType;
      const otherBoughtPlanCodes = this.boughtPlans.map(p => p.plan_code).filter(code => code !== this.currentBoughtPlan.plan_code);
      const otherBoughtPlanCapacities = this.boughtPlans.filter((p) => p.type === this.planType).map(p => p.capacity).filter(capacity => capacity !== this.currentBoughtPlan.capacity);
      return this.planMapFlat.filter((p) => filterFun(p) && !otherBoughtPlanCodes.includes(p.plan_code) && !otherBoughtPlanCapacities.includes(p.capacity));
    },
    selectableStorages() {
      return this.selectablePlanList.map((p) => {
        return {
          ...p,
          extra: {
            title: p.approxHours ? this.$t("billing.simple_edit.approx_hours", { hours: toThousands(p.approxHours) }) : "",
          },
        };
      });
    },
    selectableCapacities() {
      return sortBy(uniq(this.selectablePlanList.map((p) => p.capacity)), (c) => c);
    },
    selectableBillingCycles() {
      return this.selectablePlanList
        .filter((p) => p.capacity === this.formData.capacity && p.billing_cycle !== BILLING_CYCLE.ONE_MONTH)
        .sort((p1, p2) => {
          if (p1.billing_cycle === BILLING_CYCLE.ANNUAL && p2.billing_cycle === BILLING_CYCLE.ANNUAL_TERM_MONTHLY) {
            return -1;
          }
          if (p1.billing_cycle === BILLING_CYCLE.ANNUAL_TERM_MONTHLY && p2.billing_cycle === BILLING_CYCLE.ANNUAL) {
            return 1;
          }
          return p2.cycleValue - p1.cycleValue;
        })
        .map((p) => {
          const plan = this.currentBoughtPlan.plan_code === p.plan_code ? { ...p, isCurrent: true } : p;
          return {
            plan,
            ...this.mx_getCycleDisableInfo(plan),
          };
        });
    },
    showAnnualTermMonthly() {
      const currentPlansHasAnnualTermMonthly = this.selectablePlanList.some((item) => item.billing_cycle === BILLING_CYCLE.ANNUAL_TERM_MONTHLY);
      return (
        (enableProAnnualWithMonthlyPaymentPre ||
          enableProAnnualMonthlyPaymentPlanP2) &&
        currentPlansHasAnnualTermMonthly
      );
    },
    maxCycleValueBasePlan() {
      return (this.getCurrentBasePlanInfo?.plans || []).sort((a, b) => b.cycleValue - a.cycleValue)[0];
    },
    mx_disableAnnualTooltip() {
      const basePlanName = this.$t(this.maxCycleValueBasePlan?.typeNameKey || "");
      const addonPlanName = this.$t(_.get(this.selectablePlanList, [0, "typeNameKey"]));

      return this.$t("billing.planform.annual_addon_disable_tooltip", [
        basePlanName,
        addonPlanName,
        basePlanName,
      ]);
    },
    planTitle() {
      return this.$t(this.selectedPlan?.planNameKey);
    },
    currentUnit() {
      // Get unit from the first plan in the list, default to license
      const firstPlan = this.selectableBillingCycles[0]?.plan;
      return firstPlan?.unit ?? planUnitMap.license;
    },
    licensesLabel() {
      if (this.currentUnit === planUnitMap.license) {
        return this.$t("billing.simple_edit.number_of_licenses");
      } else if (this.currentUnit === planUnitMap.user) {
        return this.$t("billing.simplify.select_license");
      }
      return this.$t("billing.simple_edit.number_of_licenses");
    },
    licensesDesc() {
      const number = this.currentBoughtPlan.cartInfo.selected_number;
      if (this.currentUnit === planUnitMap.license) {
        return this.$t("billing.simple_edit.cur_licenses", number, { named: { number } });
      } else if (this.currentUnit === planUnitMap.user) {
        return this.$t("billing.simple_edit.cur_users", number, { named: { number } });
      }
      return this.$t("billing.simple_edit.cur_licenses", number, { named: { number } });
    },
    capacityDesc() {
      const number = this.currentBoughtPlan.capacity;
      return this.$t("billing.simple_edit.cur_participants", number, { named: { number } });
    },
    mx_getMinHosts() {
      const cases = [];
      if (this.minHostsAndReason) {
        cases.push(this.minHostsAndReason);
      }

      // Case 1: non standard price
      if (!this.isStandardPrice) {
        cases.push({
          value: this.currentBoughtLicense,
          reason: this.$t("billing.planform.meeting_license_custom_pricing"),
        });
      }

      // Case 2: block down-sell when account MRR > 500
      if (this.basicConfig?.downSellData?.disableDownSell) {
        cases.push({
          value: this.currentBoughtLicense,
          reason: this.$t("billing.error.can_not_bought_plan_tips"),
        });
      }

      // Case 3: for pending plan with cancellation coupon
      if (this.fromCancelCampaignWithPendingCase) {
        cases.push({
          value: this.currentBoughtLicense,
          reason: this.$t("billing.coupon.not_switch_plan_pending_plans"),
        });
      }

      // Compare all cases and return the one with maximum value
      if (cases.length > 0) {
        return cases.reduce((max, current) => current.value > max.value ? current : max);
      }

      return {
        value: 1,
        reason: "",
      };
    },
    mx_getMaxHosts() {
      //1.meeting plan
      if (this.selectedPlan.type === planTypes.ZOOM_MEETING) {
        if (this.selectedPlan.category === ZOOM_MEETING_SUB_TYPES.PRO) {
          return {
            value: this.currentBoughtPlan?.cartInfo.selected_number + 99,
            reason: this.$t("billing.error.cant_add_100"),
          };
        }
        return {
          value: this.licensesMax ? this.licensesMax : DEFAULT_MAX_HOST,
          reason: "",
        };
      }
      //2.addon plan
      if (this.selectedPlan?.dependBasePlanTypeList?.length) {
        if (!this.canAutoAdd) {
          const basePlanLicense = getPlanTotalLicense(this.currentBasePlans || []);
          const basePlanNameKey = get(this.currentBasePlans, [0, "parentPlanNameKey"]) || get(this.currentBasePlans, [0, "planNameKey"]) || get(this.currentBasePlans, [0, "typeNameKey"]);
          return {
            value: Math.max(basePlanLicense, this.currentBoughtLicense),
            reason: this.$t("billing.addon_license_limit_tips", [this.title, this.$t(basePlanNameKey)]),
          };
        } else {
          return {
            value: this.licensesMax || DEFAULT_MAX_HOST,
            reason: "",
          };
        }
      }

      //3.standalone plan
      if (!this.selectedPlan?.dependBasePlanTypeList?.length) {
        return {
          value: this.licensesMax ? this.licensesMax : DEFAULT_MAX_HOST,
          reason: "",
        };
      }

      return {
        value: this.licensesMax ? this.licensesMax : DEFAULT_MAX_HOST,
        reason: "",
      };
    },
    cycleAlert() {
      return this.selectableBillingCycles.find(c => c.disabled)?.disabledReason;
    },
    mx_disableAutoAdd() {
      return !simpleEditAutoAddTypes.includes(this.planType);
    },
    canAutoAdd() {
      return this.mx_canAutoAdd && !this.mx_disableAutoAdd;
    },
    trackingPlanKey() {
      let planName = getTrackingProductNameByPlanType(this.planType);
      if (this.planType === planTypes.ZOOM_MEETING) {
        planName += ` ${this.selectedPlan.category}`;
      }
      const capacity = this.showCapacity && this.formData.capacity ? " " + this.$tEn("billing.participants") : "";
      return `${planName}${capacity}`;
    },
    trackingPlanInfo() {
      const planName = getTrackingProductNameByPlanType(this.planType);
      return {
        planName: planName,
        billingCycle: TrackingBillingCycleKeys[this.formData.cycle],
        buySelection: `${TrackingBillingCycleKeys[this.formData.cycle]} - ${this.formData.capacity} ${this.$tEn("billing.participants")}`,
      };
    },
  },
  mounted() {
    this.initForm();
  },
  methods: {
    ...mapActions(["switchSelectPlanAction"]),
    initForm() {
      this.formData.capacity = this.selectedPlan.capacity;
      this.formData.licenses = this.selectedPlan.cartInfo.selected_number;
      this.formData.cycle = this.selectedPlan.billing_cycle;
      this.title = this.$t(this.selectedPlan.planNameKey);
      this.handleLicenseChange(this.formData.licenses);
    },
    handleFieldChange: _.debounce(function(field, value, isStorage) {
      const oldValue = this.formData[field];
      this.formData[field] = value;
      this.$emit("form-change", field, value, this.formData);
      this.$emit("field-change", field, value);

      if (field === "licenses") {
        this.trackLicenseChange(value, oldValue);
        this.handleLicenseChange(value, oldValue);
      } else if (field === "cycle") {
        this.trackCycleChange(value);
      } else if (field === "capacity") {
        this.trackCapacityChange(value, isStorage);
      }
    }, 200),
    handleStorageChange(value) {
      this.handleFieldChange("capacity", value, true);
    },
    handleSwitchPlan() {
      this.$emit("switch-plan", this.formData);
    },
    handleSubscriptionCycleChange(oldPlan, newPlan) {
      const newCycle = newPlan?.billing_cycle;
      this.handleFieldChange("cycle", newCycle);
    },
    handleLicenseChange(license) {
      // banner to tell user un assign
      this.mx_validDownSellLicense(license);
      if (this.mx_downSellLicenseErrorMessage) {
        this.licenseAlert = this.mx_downSellLicenseErrorMessage;
        return;
      }

      //reach max
      if (license >= this.mx_getMaxHosts.value) {
        this.licenseAlert = this.mx_getMaxHosts.reason;
        return;
      }

      //reach min
      if (license <= this.mx_getMinHosts.value) {
        this.licenseAlert = this.mx_getMinHosts.reason;
        return;
      }

      this.licenseAlert = "";
    },
    mx_getCycleDisableInfo({ cycleValue, billingCycleDependBasePlanTypeList, billing_cycle }) {
      const externalRes = this.getCycleDisableInfo?.({ billing_cycle }) ?? null;
      if (externalRes?.disabled) {
        return {
          disabled: externalRes.disabled,
          disabledReason: externalRes.reason,
        };
      }
      if (!billingCycleDependBasePlanTypeList?.length) {
        return {
          disabled: false,
          disabledReason: "",
        };
      }
      const currentBasePlanCycle = this.maxCycleValueBasePlan?.cycleValue || 0;
      const isDisabled = currentBasePlanCycle < cycleValue;
      return {
        disabled: isDisabled,
        disabledReason: isDisabled && cycleValue === 12 ? this.mx_disableAnnualTooltip : "",
      };
    },
    calculateIsOptionDisabled(plan) {
      if (this.basicConfig?.downSellData?.disableDownSell) {
        return checkMRSChange({
          oldPlan: this.currentEditPlan,
          nextPlan: plan,
          nextPlanQuantity: 1,
        }) === MRS_STATUS_TYPE_MAP.CHANGE_FUTURE;
      }
      return false;
    },
    // Event tracking methods
    trackCycleChange(billingCycle) {
      if (!billingCycle || !this.$tracking) return;

      if ([planTypes.ZM_AI_COMPANION].includes(this.planType)) {
        this.$tracking.buyInteraction(
          `subscription cycle`,
          `${this.trackingPlanKey} - ${billingCycle}`,
        );
      } else if (!this.selectedPlan?.dependBasePlanTypeList?.length) {
        this.$tracking.buyInteraction(
          `billing cycle`,
          `${this.trackingPlanKey} - toggled billed ${this.$tEn(`billing.billing_cycle.${billingCycle}`)?.toLowerCase()}`,
        );
      } else {
        this.$tracking.addOnBuyInteraction(
          `${this.trackingPlanKey} billing cycle`,
          this.$tEn(`billing.billing_cycle.${billingCycle}`)?.toLowerCase(),
        );
      }
    },
    trackLicenseChange(newLicense, oldLicense) {
      const planCode = this.selectedPlan?.plan_code;
      if (!planCode || !this.$tracking) return;

      //not addons
      if (!this.selectedPlan?.dependBasePlanTypeList?.length) {
        this.$tracking.buyInteraction(
          "number of hosts",
          `${this.trackingPlanKey} - ${newLicense > oldLicense ? "increase" : "decrease"} - ${newLicense}`,
        );
      } else if (this.planType === planTypes.WEBINAR) {
        this.$tracking.buyInteraction(
          `${this.trackingPlanInfo.planName}`,
          `${this.trackingPlanInfo.buySelection} - ${newLicense > oldLicense ? "increase" : "decrease"} - ${newLicense}`,
        );
      } else {
        this.$tracking.addOnBuyInteraction(
          `${this.trackingPlanKey} license`,
          `${this.trackingPlanKey} - ${newLicense > oldLicense ? "increase" : "decrease"} - ${newLicense}`,
        );
      }
    },
    trackCapacityChange(capacity, isStorage) {
      if (!capacity || !this.$tracking) return;

      if (isStorage) {
        const newPlan = this.selectablePlanList.find((p) => p.capacity === capacity);
        this.$tracking.addOnBuyInteraction(
          `${this.trackingPlanKey}`,
          `${newPlan?.cartCapacity}${newPlan?.cartCapacityUnit}`,
        );
      } else if (this.planType === planTypes.WEBINAR) {
        const planName = this.trackingPlanInfo.planName;
        const billingCycle = TrackingBillingCycleKeys[this.formData.cycle];
        const billingCycleUnit = TrackingBillingCycleUnitKeys[this.formData.cycle];
        this.$tracking.buyInteraction(
          `${planName}`,
          `${billingCycle} - ${capacity} ${isStorage ? "" : "attendees"}${billingCycleUnit}`,
        );
      } else {
        this.$tracking.addOnBuyInteraction(
          `${this.trackingPlanKey} attendees`,
          capacity,
        );
      }
    },
  },
};
</script>

<style lang="scss" scoped>
@import '@zoom/billing-responsive/styles/responsive.scss';
@import '@/styles/scss-utils';
@import '@/styles/variables';

.reason-tips {
  margin-top: 12px;

  :deep(p) {
    font-style: normal;
    font-weight: 400;
    margin: 0;
  }
}

.opc-simple-edit-base {
  * {
  }

  &__divider {
    height: 1px;
    width: 100%;
    background-color: var(--border-neutral, #e5e5e5);
    margin: 16px 0;
  }

  &__field {
    display: flex;
    gap: 12px;
    align-items: flex-start;
    width: 100%;
    margin-bottom: 16px;
  }

  &__subscription {
    width: 100%;
    display: flex;
    flex-direction: column;
    gap: 8px;
  }

  &__subscription-label {
    font-weight: 600;
    font-size: 16px;
    line-height: 20px;
    color: var(--zoom-color-text-stronger-neutral, #2A2B2D);
    letter-spacing: -0.31px;
    margin: 0;
  }

  &__footer {
    width: 100%;
  }

  @include media(mobile) {
    &__field {
      flex-direction: column;
      gap: 12px;
    }

    &__storage-options {
      flex-direction: column;
    }

    &__storage-card {
      min-width: 100%;
    }
  }
}
</style>
